/**
 * `feature:dashboard_runtime_summary` — aggregated tile-run health telemetry.
 *
 * A dashboard on a 30s refresh interval runs tiles thousands of times per
 * session; firing one analytics event per tile run would flood Umami and get
 * the whole channel rate-limited. Instead every run outcome increments a
 * local counter keyed by (engine, trigger, outcome, durationBucket), and the
 * counters flush as one event per non-zero key:
 *   - every FLUSH_INTERVAL_MS (5 min) while runs are being recorded,
 *   - on dashboard-tab close (useDashboardScheduler unmount),
 *   - on window unload (best-effort).
 *
 * Payload is categorical-only: engine id, the existing trigger union, a
 * closed outcome set, a coarse duration bucket, and the count. Error TEXT
 * never leaves the classifier — `classifyTileOutcome` reduces it to
 * 'timeout' / 'cancelled' / 'query_error' and drops the string.
 */
import type { TileRunResult } from '../types/dashboardRuntime';

export type TileRunTrigger = 'manual' | 'interval' | 'preview';

export type TileRunOutcome =
  | 'success'
  | 'query_error'
  | 'timeout'
  | 'cancelled'
  | 'connection_lost'
  | 'policy_blocked';

export type TileDurationBucket = '<1s' | '1-5s' | '5-30s' | '30s+' | 'n/a';

export const DASHBOARD_RUNTIME_SUMMARY_EVENT = 'feature:dashboard_runtime_summary';
export const FLUSH_INTERVAL_MS = 5 * 60 * 1000;
/**
 * Hard ceiling on emitted summary events per app run across interval AND
 * tab-close flushes (the once-per-window unload flush is exempt). Full key
 * coverage can otherwise emit dozens of events per 5-min flush indefinitely —
 * and dashboard-tab open/close churn also flushes, so without a run-level
 * budget there is no finite bound between a pathological session and Umami
 * (2026-07-25 volume audit, risk #2). Surplus keys keep aggregating and are
 * emitted by the unload flush.
 */
export const MAX_SUMMARY_EVENTS_PER_RUN = 60;

/** Reduce a settled run to its categorical outcome. Never returns the error text. */
export function classifyTileOutcome(result: TileRunResult): TileRunOutcome {
  if (result.ok) return 'success';
  if (result.needsConnection) return 'connection_lost';
  if (result.blockedByPolicy) return 'policy_blocked';
  if (/cancel/i.test(result.error)) return 'cancelled';
  if (/timeout|timed out/i.test(result.error)) return 'timeout';
  return 'query_error';
}

/** Coarse success-duration bucket; failures carry no duration → 'n/a'. */
export function bucketTileDuration(durationMs: number | undefined): TileDurationBucket {
  if (durationMs === undefined) return 'n/a';
  if (durationMs < 1_000) return '<1s';
  if (durationMs < 5_000) return '1-5s';
  if (durationMs < 30_000) return '5-30s';
  return '30s+';
}

const counts = new Map<string, number>();
let flushTimer: ReturnType<typeof setInterval> | null = null;
let unloadHookInstalled = false;
let emittedEvents = 0;

function ensureFlushMachinery(): void {
  if (!flushTimer && emittedEvents < MAX_SUMMARY_EVENTS_PER_RUN) {
    flushTimer = setInterval(flushDashboardRuntimeSummary, FLUSH_INTERVAL_MS);
  }
  if (!unloadHookInstalled && typeof window !== 'undefined') {
    unloadHookInstalled = true;
    window.addEventListener('beforeunload', flushOnUnload);
  }
}

/** Unload path — emits everything left, exempt from the run budget (fires at most once). */
function flushOnUnload(): void {
  emitCounters(false);
}

/** Record one settled tile run. Aggregates locally — NEVER fires per run. */
export function recordTileRunOutcome(
  engine: string,
  trigger: TileRunTrigger,
  result: TileRunResult,
): void {
  const outcome = classifyTileOutcome(result);
  const durationBucket = bucketTileDuration(result.ok ? result.durationMs : undefined);
  const key = `${engine}|${trigger}|${outcome}|${durationBucket}`;
  counts.set(key, (counts.get(key) ?? 0) + 1);
  ensureFlushMachinery();
}

/**
 * Emit one summary event per non-zero counter key, then reset the emitted
 * keys. Budgeted: stops at MAX_SUMMARY_EVENTS_PER_RUN — surplus keys stay in
 * the counter map (still aggregating) for the unload flush, and the interval
 * timer is cleared once the budget is spent.
 */
export function flushDashboardRuntimeSummary(): void {
  emitCounters(true);
  if (emittedEvents >= MAX_SUMMARY_EVENTS_PER_RUN && flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

function emitCounters(budgeted: boolean): void {
  for (const [key, count] of counts) {
    if (budgeted && emittedEvents >= MAX_SUMMARY_EVENTS_PER_RUN) return;
    counts.delete(key);
    emittedEvents += 1;
    const [engine, trigger, outcome, durationBucket] = key.split('|');
    window.electronAPI?.analytics
      ?.track(DASHBOARD_RUNTIME_SUMMARY_EVENT, { engine, trigger, outcome, durationBucket, count })
      .catch(() => {});
  }
}

/**
 * Per-app-run cap on `feature:dashboard_alert_fired` (2026-07-25 volume
 * audit long-tail). An alert flapping around its threshold on a short
 * refresh interval with cooldown 0 fires on every armed→firing cross —
 * unbounded over a long session. The desktop notification itself is NOT
 * capped, only the analytics event.
 */
export const MAX_ALERT_FIRED_EVENTS_PER_RUN = 20;
let alertFiredEmitted = 0;

export function trackDashboardAlertFired(
  track: (name: string, props: Record<string, boolean>) => void,
  background: boolean,
): void {
  if (alertFiredEmitted >= MAX_ALERT_FIRED_EVENTS_PER_RUN) return;
  alertFiredEmitted += 1;
  track('feature:dashboard_alert_fired', { background });
}

/** Test-only: reset counters, timers, and the run budgets. */
export function _resetDashboardRuntimeTelemetryForTests(): void {
  counts.clear();
  emittedEvents = 0;
  alertFiredEmitted = 0;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (unloadHookInstalled && typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', flushOnUnload);
    unloadHookInstalled = false;
  }
}
